import { useEffect, useRef } from 'react'
import {
  CONNECTION_TYPES,
  widthClass,
  type TelemetryApp,
  type TelemetryError,
  type TelemetrySession,
} from '@jupi/shared'
import { apiUrl } from './api/client'
import { useAuth } from './auth/AuthContext'

// The client beacon and frontend error capture (see packages/shared's
// telemetry.ts for what is sent, and why it is bucketed). Both are
// fire-and-forget: a failed report is dropped, never retried and never
// shown to the member.

function send(path: string, body: TelemetrySession | TelemetryError) {
  try {
    void fetch(apiUrl(path), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // Lets an error report from a page that is unloading still go out.
      keepalive: true,
    }).catch(() => {})
  } catch {
    // fetch itself threw (no network stack at all) -- nothing to do.
  }
}

function sessionBeacon(app: TelemetryApp, signedIn: boolean): TelemetrySession {
  const effectiveType = (navigator as { connection?: { effectiveType?: string } }).connection?.effectiveType
  return {
    app,
    screen: widthClass(window.screen.width),
    viewport: widthClass(window.innerWidth),
    dpr: Math.min(5, Math.max(0.5, Math.round(window.devicePixelRatio * 2) / 2)),
    prefersDark: window.matchMedia('(prefers-color-scheme: dark)').matches,
    standalone:
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as { standalone?: boolean }).standalone === true,
    online: navigator.onLine,
    connection: (CONNECTION_TYPES as readonly string[]).includes(effectiveType ?? '')
      ? (effectiveType as TelemetrySession['connection'])
      : null,
    signedIn,
  }
}

// A crash loop must not turn into a report loop: at most this many per
// page load, and each distinct message once. The server throttles per IP
// on top of this.
const MAX_ERRORS_PER_PAGE = 5

function scriptPath(url: string | undefined): string | null {
  if (!url) return null
  try {
    return new URL(url, window.location.href).pathname.slice(0, 300)
  } catch {
    return null
  }
}

function installErrorCapture(app: TelemetryApp): () => void {
  const seen = new Set<string>()
  const report = (kind: TelemetryError['kind'], error: unknown, event?: ErrorEvent) => {
    const message = (error instanceof Error ? error.message : event?.message || String(error)).slice(0, 500)
    if (seen.size >= MAX_ERRORS_PER_PAGE || seen.has(message)) return
    seen.add(message)
    send('/api/telemetry/error', {
      app,
      kind,
      message,
      source: scriptPath(event?.filename),
      line: event?.lineno || null,
      column: event?.colno || null,
      stack: error instanceof Error && error.stack ? error.stack.slice(0, 4000) : null,
      route: window.location.pathname.slice(0, 200),
    })
  }
  const onError = (event: ErrorEvent) => report('error', event.error, event)
  const onRejection = (event: PromiseRejectionEvent) => report('unhandledrejection', event.reason)
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}

// Mounted once inside AuthProvider. Sends the beacon when the app starts
// and again when the member signs in, and reports uncaught errors for as
// long as the app is open.
export function Telemetry({ app }: { app: TelemetryApp }) {
  const { user, loading } = useAuth()
  const signedIn = Boolean(user)
  const sentFor = useRef<boolean | null>(null)

  useEffect(() => installErrorCapture(app), [app])

  useEffect(() => {
    // Wait for the session check, so a returning member counts as signed in.
    if (loading) return
    // Once at start-up, and once more on signing in; signing out sends nothing.
    if (sentFor.current === true || sentFor.current === signedIn) return
    sentFor.current = signedIn
    send('/api/telemetry/session', sessionBeacon(app, signedIn))
  }, [app, loading, signedIn])

  return null
}
